import {
  FloatingPortal,
  Placement,
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useMergeRefs,
  useRole,
  useTransitionStyles,
} from "@floating-ui/react";
import * as Sentry from "@sentry/react";
import DOMPurify from "dompurify";
import {
  HTMLProps,
  cloneElement,
  createContext,
  forwardRef,
  isValidElement,
  useContext,
  useMemo,
  useState,
} from "react";

import AppError from "@components/error/error";

export function useTooltip({
  placement = "top",
  delay,
}: {
  placement?: Placement;
  delay?: number;
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);

  const data = useFloating({
    placement,
    open: uncontrolledOpen,
    onOpenChange: setUncontrolledOpen,
    whileElementsMounted: autoUpdate,
    middleware: [offset(5), flip(), shift()],
  });

  const context = data.context;
  const hover = useHover(context, {
    move: false,
    enabled: true,
    delay: 100,
    restMs: delay,
  });
  const focus = useFocus(context, { enabled: true });
  const dismiss = useDismiss(context);
  const click = useClick(context, {
    enabled: true,
    stickIfOpen: false,
    toggle: true,
  });
  const role = useRole(context, { role: "tooltip" });
  const interactions = useInteractions([hover, click, focus, dismiss, role]);

  return useMemo(
    () => ({
      open: uncontrolledOpen,
      setOpen: setUncontrolledOpen,
      ...interactions,
      ...data,
    }),
    [uncontrolledOpen, setUncontrolledOpen, interactions, data]
  );
}

type ContextType = ReturnType<typeof useTooltip> | null;

const TooltipContext = createContext<ContextType>(null);

const useTooltipState = () => {
  const context = useContext(TooltipContext);
  if (context == null) {
    throw new Error("Tooltip components must be wrapped in <Tooltip />");
  }
  return context;
};

const TooltipTrigger = forwardRef<HTMLElement, HTMLProps<HTMLElement>>(
  function TooltipTrigger({ children, ...props }, propRef) {
    const state = useTooltipState();

    const childrenRef = (children as any).ref;
    const ref = useMergeRefs([state.refs.setReference, propRef, childrenRef]);

    if (isValidElement(children)) {
      return cloneElement(
        children,
        state.getReferenceProps({
          ref,
          ...props,
          ...children.props,
          "data-state": state.open ? "open" : "closed",
        })
      );
    }

    if (import.meta.env.DEV) {
      throw new Error("TooltipTrigger must be a valid React element");
    } else {
      Sentry.captureException("TooltipTrigger must be a valid React element");
      return children;
    }
  }
);

const TooltipContent = forwardRef<HTMLDivElement, { children: string }>(
  function TooltipContent({ children, ...props }, propRef) {
    const state = useTooltipState();
    const ref = useMergeRefs([state.refs.setFloating, propRef]);

    const { isMounted, styles } = useTransitionStyles(state.context, {
      duration: 0,
      initial: { opacity: 0 },
    });

    if (!isMounted) return null;
    const cleanHtml =
      typeof children === "string" ? DOMPurify.sanitize(children) : children;

    return (
      <FloatingPortal>
        <div
          ref={ref}
          className="bg-black/80 text-white rounded-md text-sm py-1 px-2 max-w-64 z-tooltip whitespace-pre-wrap"
          style={{
            ...state.floatingStyles,
            ...styles,
          }}
          dangerouslySetInnerHTML={{ __html: cleanHtml }}
          {...state.getFloatingProps(props)}
        />
      </FloatingPortal>
    );
  }
);

export const Tooltip = ({
  children,
  text,
  placement = "top",
  delay = 300,
}: {
  children: any;
  text?: null | string;
  placement?: Placement;
  delay?: number;
}) => {
  const tooltip = useTooltip({ placement, delay });

  if (isValidElement(children) && typeof children.type !== "string") {
    if (
      typeof children.type === "function" ||
      typeof children.type === "object"
    ) {
      if (import.meta.env.DEV) {
        throw new Error(
          `Wrap the child of Tooltip in an HTML element: ${JSON.stringify(
            (children as any)._source
          )}`
        );
      }
    }
    return children;
  }

  if (!text) {
    return children;
  }

  return (
    <TooltipContext.Provider value={tooltip}>
      <TooltipTrigger>{children}</TooltipTrigger>
      <TooltipContent>{text}</TooltipContent>
    </TooltipContext.Provider>
  );
};

export default Sentry.withErrorBoundary(Tooltip, {
  fallback: <AppError description={"The tooltip could not be displayed."} />,
});
